import React from 'react';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { User } from '@/types';

interface DashboardHeaderProps {
  user: User;
  onPasswordChange: () => void;
  onLogout: () => void;
  onSettingsClick: () => void;
}

const ShootingStars: React.FC = React.memo(() => {
  const stars = React.useMemo(() => {
    const star1Top = Math.random() * 50;
    const star1Left = 40 + Math.random() * 50;
    const star2Top = Math.random() * 50;
    const star2Left = 40 + Math.random() * 50;
    return { star1Top, star1Left, star2Top, star2Left };
  }, []);

  return (
    <>
      <div 
        className="shooting-star" 
        style={{ 
          top: `${stars.star1Top}%`, 
          left: `${stars.star1Left}%`, 
          animationDelay: '0s',
          animationDuration: '4s'
        }}
      ></div>
      <div 
        className="shooting-star" 
        style={{ 
          top: `${stars.star2Top}%`, 
          left: `${stars.star2Left}%`, 
          animationDelay: '2s',
          animationDuration: '5s'
        }}
      ></div>
    </>
  );
});

ShootingStars.displayName = 'ShootingStars';

export const DashboardHeader: React.FC<DashboardHeaderProps> = ({
  user,
  onPasswordChange,
  onLogout,
  onSettingsClick,
}) => {
  const getRoleLabel = () => {
    if (user.role === 'saas_admin') return 'SaaS 관리자';
    if (user.role === 'admin') return '기업 관리자';
    if (user.role === 'user') {
      return user.permission === 'readonly' 
        ? '일반 사용자 (읽기전용)' 
        : '일반 사용자 (읽기/쓰기)';
    }
    return '';
  };

  return (
    <header className="header-cosmic sticky top-0 z-50 overflow-hidden relative">
      <div className="absolute inset-0 pointer-events-none">
        <div className="cosmic-star" style={{ top: '20%', left: '15%', animationDelay: '0s' }}></div>
        <div className="cosmic-star" style={{ top: '60%', left: '35%', animationDelay: '0.5s' }}></div>
        <div className="cosmic-star" style={{ top: '30%', left: '55%', animationDelay: '1s' }}></div>
        <div className="cosmic-star" style={{ top: '70%', left: '75%', animationDelay: '0.3s' }}></div>
        <div className="cosmic-star" style={{ top: '40%', left: '92%', animationDelay: '0.8s' }}></div>
        <ShootingStars />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex justify-between items-center py-4">
          <div className="flex items-center">
            <div className="h-10 w-10 mr-3">
              <svg viewBox="0 0 40 40" className="w-full h-full">
                <defs>
                  <linearGradient id="headerLogoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#667eea"/>
                    <stop offset="50%" stopColor="#764ba2"/>
                    <stop offset="100%" stopColor="#f093fb"/>
                  </linearGradient>
                </defs>
                <circle cx="20" cy="20" r="16" stroke="url(#headerLogoGrad)" strokeWidth="2" fill="none"/>
                <circle cx="20" cy="20" r="6" fill="url(#headerLogoGrad)"/>
                <circle cx="20" cy="8" r="3" fill="#667eea" className="logo-orbit"/>
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">GenAI Labs</h1>
              <p className="text-sm text-gray-300">
                {user.customer?.customer_name || 'SaaS 관리자'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center space-x-2 text-white hover:bg-white/10">
                  <div className="h-8 w-8 bg-gradient-to-br from-[#6366f1] to-[#8b5cf6] rounded-full flex items-center justify-center">
                    <span className="text-white font-medium text-sm">
                      {user.email.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className="text-sm font-medium text-white">{user.name || user.email}</p>
                    <p className="text-xs text-gray-300">{getRoleLabel()}</p>
                  </div>
                  <svg className="w-4 h-4 text-white/70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div>
                    <p className="font-medium">{user.name || '사용자'}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user.auth_type !== 'oauth' && (
                  <DropdownMenuItem onClick={onPasswordChange}>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    비밀번호 변경
                  </DropdownMenuItem>
                )}
                {user.auth_type === 'oauth' && (
                  <DropdownMenuItem disabled className="text-gray-400">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Google 계정<br />(비밀번호 변경 불가)
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} className="text-red-600">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  로그아웃
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              onClick={onSettingsClick}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
              title="설정"
            >
              <svg className="w-5 h-5 text-white/70 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

